import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import {
  createContext,
  useContext,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

import { LiveRegion } from './LiveRegion';
import { ItemContext, type SortableMoves } from './sortableItemContext';

/** How to pick up, move and drop with the keyboard, read once per handle. */
const KEYBOARD_INSTRUCTIONS =
  'To move it with the keyboard, press Space, use the up and down arrow keys, and press Space again to drop it. Press Escape to cancel. Or use the Move up and Move down buttons.';

interface ListContextValue {
  ids: string[];
  nameOf: (id: string) => string;
  move: (id: string, delta: -1 | 1) => void;
  disabled: boolean;
}

const ListContext = createContext<ListContextValue | null>(null);

function useList(): ListContextValue {
  const list = useContext(ListContext);
  if (!list) throw new Error('A SortableItem must sit inside a SortableList.');
  return list;
}

interface SortableListProps {
  /** The entries' ids, in their current order. */
  ids: string[];
  /** An entry's name, said in announcements and on its buttons. */
  nameOf: (id: string) => string;
  /** Called with the whole new order after a drag or a Move button. */
  onReorder: (ids: string[]) => void;
  /** Read-only: nothing can be moved. */
  disabled?: boolean;
  children: ReactNode;
}

/**
 * A list whose entries can be put in a new order three ways: dragged with a
 * pointer, moved with the keyboard on the drag handle (Space, the arrow keys,
 * Space), or moved one place with the Move up and Move down buttons. Every
 * move is announced, so a screen reader hears where the entry went
 * ("Campaigns moved to position 2 of 6"). The repository's rule: a drag never
 * ships without its keyboard and button equivalents.
 *
 * The caller owns the order: `onReorder` gets the whole new list, and the
 * caller decides whether to show it at once (and put it back on refusal).
 */
export function SortableList({
  ids,
  nameOf,
  onReorder,
  disabled = false,
  children,
}: SortableListProps) {
  const [said, setSaid] = useState('');
  // The order when the drag began. Positions are announced from it: by the
  // time the drop is announced, the caller may already show the new order,
  // and the dropped entry's place in that is not where it was dropped.
  const [atStart, setAtStart] = useState<string[] | null>(null);
  const sensors = useSensors(
    // A few pixels before a press becomes a drag, so a click on the handle
    // is still a click.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const name = (id: UniqueIdentifier) => nameOf(String(id));
  const place = (id: UniqueIdentifier) => {
    const order = atStart ?? ids;
    return `position ${String(order.indexOf(String(id)) + 1)} of ${String(order.length)}`;
  };

  const announcements: Announcements = {
    onDragStart: ({ active }) => `Picked up ${name(active.id)}, ${place(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over
        ? `${name(active.id)} is over ${place(over.id)}.`
        : `${name(active.id)} is no longer over the list.`,
    onDragEnd: ({ active, over }) =>
      over
        ? `${name(active.id)} moved to ${place(over.id)}.`
        : `${name(active.id)} was dropped where it was.`,
    onDragCancel: ({ active }) =>
      `Moving cancelled. ${name(active.id)} is back at ${place(active.id)}.`,
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(ids, from, to));
  };

  const move = (id: string, delta: -1 | 1) => {
    const from = ids.indexOf(id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= ids.length) return;
    onReorder(arrayMove(ids, from, to));
    setSaid(
      `${nameOf(id)} moved to position ${String(to + 1)} of ${String(ids.length)}.`,
    );
  };

  return (
    <ListContext.Provider value={{ ids, nameOf, move, disabled }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={() => setAtStart(ids)}
        onDragEnd={onDragEnd}
        accessibility={{
          announcements,
          screenReaderInstructions: { draggable: KEYBOARD_INSTRUCTIONS },
        }}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {children}
        </SortableContext>
      </DndContext>
      <LiveRegion message={said} />
    </ListContext.Provider>
  );
}

/** What a `SortableItem` hands the element it wraps. */
export interface SortableItemHooks {
  /** Put on the element that moves: the card, or the table row. */
  ref: (element: HTMLElement | null) => void;
  style: CSSProperties;
  dragging: boolean;
}

interface SortableItemProps {
  id: string;
  /** Prefix for the handle's IDs: `<testId>-drag-button`, `-move-up-button`, `-move-down-button`. */
  testId: string;
  /**
   * False when the entry's own menu offers Move up and Move down instead
   * (`useSortableMoves`), so the handle is the drag handle alone. The moves
   * are still there for anybody who cannot drag.
   */
  moveButtons?: boolean;
  children: (hooks: SortableItemHooks) => ReactNode;
}

/**
 * One entry of a `SortableList`. The element it wraps takes `ref` and
 * `style`; a `SortableHandle` anywhere inside it (a card's header, a table
 * cell) is that entry's handle.
 */
export function SortableItem({
  id,
  testId,
  moveButtons = true,
  children,
}: SortableItemProps) {
  const list = useList();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: list.disabled });

  const index = list.ids.indexOf(id);
  const name = list.nameOf(id);
  const moves: SortableMoves = {
    moveUp: () => list.move(id, -1),
    moveDown: () => list.move(id, 1),
    canMoveUp: !list.disabled && index > 0,
    canMoveDown: !list.disabled && index < list.ids.length - 1,
  };

  // Built here, beside the ref it needs, and handed down finished, so a
  // handle can sit anywhere inside the entry: a card's header, a table cell.
  const handle = (
    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      <IconButton
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        size="small"
        aria-label={`Drag ${name}`}
        disabled={list.disabled}
        data-testid={`${testId}-drag-button`}
        sx={{ cursor: list.disabled ? 'default' : 'grab', touchAction: 'none' }}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      {moveButtons ? (
        <>
          <IconButton
            size="small"
            aria-label={`Move ${name} up`}
            disabled={!moves.canMoveUp}
            onClick={moves.moveUp}
            data-testid={`${testId}-move-up-button`}
          >
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Move ${name} down`}
            disabled={!moves.canMoveDown}
            onClick={moves.moveDown}
            data-testid={`${testId}-move-down-button`}
          >
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
        </>
      ) : null}
    </Box>
  );

  return (
    <ItemContext.Provider value={{ handle, moves }}>
      {children({
        ref: setNodeRef,
        style: {
          transform: CSS.Transform.toString(transform),
          transition,
          position: 'relative',
          zIndex: isDragging ? 2 : undefined,
          opacity: isDragging ? 0.85 : undefined,
        },
        dragging: isDragging,
      })}
    </ItemContext.Provider>
  );
}

/**
 * The drag handle, with Move up and Move down beside it, for the
 * `SortableItem` it sits in. Each is named for the entry ("Move Campaigns
 * up"), and the end buttons are disabled where there is nowhere to go.
 */
export function SortableHandle() {
  return <>{useContext(ItemContext)?.handle}</>;
}
